import React, { useRef, useState, useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { useTextSearch } from 'frontastic/hooks/useSearch';
import CloseIcon from '../../../../icons/close';
import SearchIcon from '../../../../icons/search';
import Image from 'next/image';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import Link from 'next/link';
import Loading from 'icons/loop';
import { RouteHelpers } from 'helpers/routerHelpers';
import { SortAttributes } from 'frontastic/tastics/products/product-list/components/ProductListSort';
import { useRouter } from 'next/navigation';
import { Product } from 'shared/types/product/Product';
import { Variant } from 'shared/types/product/Variant';
import useOnClickOutside from 'helpers/hooks/useOnClickOutside';
import ChevronRight from 'icons/chevron-right';
import { cn } from 'helpers/utils/classnames';

export interface Props {
  className?: string;
  closeSearch?: () => void;
}

const MAX_RESULTS = 4;
const QUICKSEARCH_QUERY = {
  offset: 0,
  sort: SortAttributes[0],
  activeFacets: [],
  limit: MAX_RESULTS,
};

const Quicksearch: React.FC<Props> = ({ className, closeSearch }) => {
  const router = useRouter();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [searchInput, setSearchInput] = useState('');
  const [isDesktop] = useMediaQuery(desktop);
  const { textSearch, textSearchLoading, textSearchResults, clearTextSearch } = useTextSearch(
    searchInput,
    QUICKSEARCH_QUERY,
    'quicksearch',
  );
  const [hasSearched, setHasSearched] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Debounced search function that will be stable across renders
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      if (searchTerm.trim()) {
        textSearch(searchTerm);
        setHasSearched(true);
        setShowSearchResults(true);
      } else {
        clearTextSearch();
        setHasSearched(false);
        setShowSearchResults(false);
      }
    }, 500),
    [textSearch, clearTextSearch],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useOnClickOutside(
    searchContainerRef,
    () => {
      setShowSearchResults(false);
    },
    showSearchResults,
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchQuery = e.target.value;
      setSearchInput(searchQuery);
      debouncedSearch(searchQuery);
    },
    [debouncedSearch],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && searchInput.trim()) {
        event.preventDefault();
        setSearchInput('');
        setShowSearchResults(false);
        router.push(`/search${RouteHelpers.encodeToURL({ q: searchInput.trim() })}`);
      }
    },
    [searchInput, router],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    clearTextSearch();
    setHasSearched(false);
    setShowSearchResults(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [clearTextSearch]);

  const handleViewMoreClick = useCallback(() => {
    setSearchInput('');
    setShowSearchResults(false);
  }, []);

  const handleProductClick = useCallback(() => {
    setSearchInput('');
    setShowSearchResults(false);
  }, []);

  return (
    <section data-testid="desktop-quicksearch" className={className} role="search" aria-label="Site search">
      <div className="relative" ref={searchContainerRef}>
        <button
          type="button"
          onClick={() => textSearch(searchInput)}
          id="searchBtn"
          aria-label="Search for products"
          className="absolute left-[20px] top-[8px] h-6 w-6"
        >
          <SearchIcon className="size-4" aria-hidden="true" />
        </button>
        <label htmlFor="headerSearch" className="sr-only">
          Search
        </label>
        <input
          id="headerSearch"
          ref={searchInputRef}
          type="text"
          autoComplete="off"
          value={searchInput}
          onChange={handleSearchChange}
          onKeyDown={handleKeyPress}
          className="h-10 w-full rounded-md border-0 border-transparent bg-white py-2 pl-[50px] pr-9 placeholder:text-gray-400 focus:border-none focus:outline-none focus:ring-0"
          placeholder={isDesktop ? 'Search' : ''}
          aria-label="Search"
          aria-describedby="searchHelpText"
        />
        <span id="searchHelpText" className="sr-only">
          Type to search products
        </span>
        {searchInput && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute right-[12px] top-[14px] size-3"
            aria-label="Clear search"
          >
            <CloseIcon className="size-3" aria-hidden="true" />
          </button>
        )}

        {textSearchResults && textSearchResults.data && searchInput && showSearchResults && (
          <div
            id="search-results"
            className="absolute left-0 right-0 z-[999] max-h-[calc(100vh-var(--header-mobile-size)-40px)] overflow-y-auto bg-neutral-255 p-4 shadow-lg lg:left-auto lg:right-auto lg:min-h-[100px] lg:min-w-[640px] lg:rounded-md lg:pt-10"
            aria-live="polite"
            role="region"
            aria-label="Search results"
          >
            {textSearchLoading ? (
              <div className="flex justify-center p-4" aria-live="polite">
                <Loading className="h-6 w-6 animate-spin" aria-hidden="true" />
                <span className="sr-only">Loading search results</span>
              </div>
            ) : (
              <>
                {textSearchResults.data &&
                'items' in textSearchResults.data &&
                Array.isArray(textSearchResults.data.items) &&
                textSearchResults.data.items.length > 0 ? (
                  <>
                    <ul
                      className="grid grid-cols-[repeat(2,1fr)] gap-5 lg:w-full lg:grid-cols-[repeat(4,1fr)_0.5fr]"
                      role="presentation"
                    >
                      {textSearchResults.data.items.map((product: Product, index: number) => {
                        if (!product) return null;
                        return (
                          <li
                            key={`product-${index}`}
                            className={cn(
                              'flex w-full flex-col items-center justify-start',
                              index > 1 && 'hidden lg:flex',
                            )}
                            role="option"
                          >
                            <Link
                              href={product._url || '#'}
                              className="w-full"
                              onClick={handleProductClick}
                              aria-label={`View product: ${product.name}`}
                            >
                              {product.masterVariant &&
                              product.masterVariant.images &&
                              product.masterVariant.images[0] ? (
                                <div className="relative flex w-full items-center justify-center rounded-md bg-gray-200 pb-[100%]">
                                  <Image
                                    src={
                                      product.variants?.filter((variant: Variant) => variant.isMatchingVariant)?.[0]
                                        ?.images?.[0] || product.masterVariant.images[0]
                                    }
                                    loader={amplienceImageLoader}
                                    alt={product.name || 'Product image'}
                                    sizes="(max-width: 1024px) 50vw, 100px"
                                    className="rounded-md object-cover"
                                    fill
                                  />
                                </div>
                              ) : (
                                <div className="relative flex w-full items-center justify-center rounded-md bg-gray-200 pb-[100%]">
                                  <span className="absolute text-xs text-gray-500">No image</span>
                                </div>
                              )}
                              <p className="mt-4 w-full text-left text-sm font-normal">
                                {product.name || 'Unnamed product'}
                              </p>
                            </Link>
                          </li>
                        );
                      })}
                      <div className="hidden items-center justify-center pb-12 lg:flex">
                        <Link
                          href={`/search${RouteHelpers.encodeToURL({ q: searchInput?.trim() })}`}
                          className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-lightGray"
                          onClick={handleViewMoreClick}
                          aria-label={`View all results for "${searchInput?.trim()}"`}
                        >
                          <ChevronRight className="fill-white" />
                        </Link>
                      </div>
                    </ul>
                    <div className="flex justify-center">
                      <Link
                        href={`/search${RouteHelpers.encodeToURL({ q: searchInput?.trim() })}`}
                        className="mt-8 inline-flex justify-center rounded-lg bg-primary-lightGray px-5 py-3 text-center text-sm font-bold text-white lg:mb-0 lg:mt-6"
                        onClick={handleViewMoreClick}
                        aria-label={`View all results for "${searchInput?.trim()}"`}
                      >
                        View all
                      </Link>
                    </div>
                  </>
                ) : (
                  hasSearched &&
                  !textSearchLoading && (
                    <div className="mt-2 p-4 text-center text-sm" aria-live="polite">
                      <p>No results for &quot;{searchInput}&quot;</p>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Quicksearch;
